/********************************************************************************
 * Copyright (c) 2020 Cedalo GmbH
 *
 * This program and the accompanying materials are made available under the
 * terms of the Eclipse Public License 2.0 which is available at
 * http://www.eclipse.org/legal/epl-2.0.
 *
 * SPDX-License-Identifier: EPL-2.0
 *
 ********************************************************************************/

import React, { useEffect, useState } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as Actions from '../../../actions/actions';
import SettingsPaper from '../../utils/SettingsPaper';
import { FormattedMessage } from 'react-intl';
import FormGroup from '@mui/material/FormGroup';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import { graphManager } from '../../../GraphManager';
import { Reference } from '@cedalo/parser';
import Grid from '@mui/material/Grid';
import { withAppData } from '../../app/AppProvider';
import {useJSGNotification} from '../../utils/JSGHooks';
import JSG from '@cedalo/jsg-ui';

const isNameInUse = (name, sheetModel) => {
	const sheet = graphManager.getStreamSheetByName(name);
	return sheet && sheet !== sheetModel;
};

function SheetSettingsComponent(props) {
	const { sheetId } = props;
	const sheetModel = sheetId ? graphManager.graph.streamSheetsContainer.find(sheetId) : undefined;
	const [name, setName] = useState(sheetModel ? sheetModel.name : '');
	const [nameError, setNameError] = useState({});
	const [columns, setColumns] = useState(sheetModel ? sheetModel.sheet.columns.maxcol : 0);
	const [rows, setRows] = useState(sheetModel ? sheetModel.sheet.maxRows : 0);
	const [maxChars, setMaxChars] = useState(sheetModel ? sheetModel.properties.maxChars : 0);
	const renderOnNotification = useState(null)[1];
	const connections = props.connections ? props.connections.map((conn) => conn.name) : [];

	const handleClose = () => {
		props.close();
	};

	const onSettingsNotification = () => renderOnNotification({});
	useJSGNotification('streamsheet_settings_notification', onSettingsNotification);

	const handleSetting = (setting) => {
		props.saveProcessSettings({
			machineId: graphManager.graph.machineContainer.machineId,
			streamsheetId: sheetId,
			...setting
		});
	};

	const validateSheetName = (newName) => {
		let error = {};
		if (JSG.Numbers.canBeNumber(newName)) {
			error = {
				id: 'Reference.InvalidNameNumber',
				message: 'A sheet must contain a character.'
			};
		} else if (newName.length > 31) {
			error = {
				id: 'Reference.InvalidNameLength',
				message: 'Sheet names can not be longer than 31 characters.'
			};
		} else if (!Reference.isValidIdentifier(newName)) {
			error = {
				id: 'Reference.InvalidName',
				message: 'Only alphanumeric characters and the underscore are allowed'
			};
		} else if (connections.includes(newName)) {
			error = {
				id: 'SheetSettings.NameInUseByConnection',
				message: 'A connection with same name already in use. Please select a different name!'
			};
		} else if (isNameInUse(newName, sheetModel)) {
			error = {
				id: 'SheetSettings.NameInUse',
				message: 'Name already in use, please select unique name!'
			};
		}
		setName(newName);
		setNameError(error);
		return error.id ? undefined : newName;
	}
	const setSheetName = (event) => {
		const newName = validateSheetName(event.target.value);
		if (newName) handleSetting({ name: newName });
	}

	useEffect(() => {
		if (sheetModel) {
			setName(sheetModel.name);
			setColumns(sheetModel.sheet.columns.maxcol);
			setRows(sheetModel.sheet.maxRows);
			setMaxChars(sheetModel.properties.maxChars);
			validateSheetName(sheetModel.name);
		}
	}, [sheetId]);

	if (!props.open) {
		return null;
	}

	return (
		<SettingsPaper
			open={props.open}
			close={handleClose}
			title={<FormattedMessage id="SheetSettings.Title" defaultMessage="Sheet Settings" />}
			helpContext="guides/sheets/sheetsettings"
		>
			<FormGroup>
				<TextField
					label={<FormattedMessage id="SheetSettings.Name" defaultMessage="Name" />}
					variant="outlined"
					size="small"
					margin="normal"
					fullWidth
					onBlur={setSheetName}
					onChange={(event) => validateSheetName(event.target.value)}
					// onChange={(event) => setName(event.target.value)}
					value={name}
					// error={!Reference.isValidIdentifier(name)}
					error={nameError.id}
					helperText={nameError.id ? <FormattedMessage id={nameError.id} defaultMessage={nameError.message} /> : ''}
				/>
				{sheetModel.properties.sheetType === 'dashboard' ? null : [
				<Grid container direction="row" key="SheetSizes" spacing={2}>
					<Grid xs={6} item>
						<TextField
							id="number"
							variant="outlined"
							size="small"
							margin="normal"
							fullWidth
							label={<FormattedMessage id="SheetSettings.Columns" defaultMessage="Columns" />}
							inputProps={{
								min: 1,
								max: 50,
								step: 1
							}}
							error={columns > 50 || columns < 1}
							helperText={
								columns > 50 || columns < 1 ? (
									<FormattedMessage
										id="SheetSettings.TooManyColumns"
										defaultMessage="Only 50 columns allowed!"
									/>
								) : (
									''
								)
							}
							value={columns}
							onBlur={(event) => {
								const cols = Number(event.target.value);
								if (cols > 0 && cols < 51) {
									handleSetting({ sheet: { maxcol: cols } });
								}
							}}
							onChange={(event) => setColumns(Number(event.target.value))}
							type="number"
						/>
					</Grid>
					<Grid xs={6} item>
						<TextField
							variant="outlined"
							size="small"
							fullWidth
							margin="normal"
							id="number"
							label={<FormattedMessage id="SheetSettings.Rows" defaultMessage="Rows" />}
							inputProps={{
								min: 1,
								max: 1000,
								step: 1
							}}
							error={rows > 1000 || rows < 1}
							helperText={
								rows > 1000 || rows < 1 ? (
									<FormattedMessage
										id="SheetSettings.TooManyRows"
										defaultMessage="Only 1000 rows allowed!"
									/>
								) : (
									''
								)
							}
							value={rows}
							onBlur={(event) => {
								const maxrow = Number(event.target.value);
								if (maxrow > 0 && maxrow <= 1000) {
									handleSetting({ sheet: { maxrow } });
								}
							}}
							onChange={(event) => setRows(Number(event.target.value))}
							type="number"
						/>
					</Grid>
				</Grid>,
				<FormGroup key="SheetOptions">
					<FormControlLabel
						control={
							<Checkbox
								checked={sheetModel.sheet.showGrid}
								onChange={(event, state) => handleSetting({ settings: { showGrid: state } })}
							/>
						}
						label={<FormattedMessage id="SheetSettings.ShowGrid" defaultMessage="Show Grid" />}
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={sheetModel.sheet.showHeader}
								onChange={(event, state) => handleSetting({ settings: { showHeader: state } })}
							/>
						}
						label={<FormattedMessage id="SheetSettings.ShowHeaders" defaultMessage="Show Headers" />}
					/>
					<FormControlLabel
						control={
							<Checkbox
								checked={sheetModel.sheet.showFormulas}
								onChange={(event, state) => handleSetting({ settings: { showFormulas: state } })}
							/>
						}
						label={<FormattedMessage id="SheetSettings.ShowFormulas" defaultMessage="Show Formulas" />}
					/>
					<FormControlLabel
						control={
						<Checkbox
							checked={sheetModel.sheet.greyIfRows}
							onChange={(event, state) => handleSetting({ settings: { greyIfRows: state } })}
						/>
					}
						label={<FormattedMessage id="SheetSettings.GreyIfRows" defaultMessage="Grey IF Rows" />}
					/>
					{/*<FormControlLabel*/}
					{/*	style={{*/}
					{/*		marginTop: '15px'*/}
					{/*	}}*/}
					{/*	control={*/}
					{/*		<Checkbox*/}
					{/*			checked={preferences.sheetProtect}*/}
					{/*			onChange={this.handleSheetProtect}*/}
					{/*		/>*/}
					{/*	}*/}
					{/*	label={*/}
					{/*		<FormattedMessage id="ProtectSheet" defaultMessage="Protect Sheet" />*/}
					{/*	}*/}
					{/*/>*/}
					<div style={{ marginBottom: '5px' }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={maxChars > 0}
									onChange={(event, state) => {
										setMaxChars(state ? 1000 : -1);
										handleSetting({ sheet: { maxchars: state ? 1000 : -1 } });
									}}
								/>
							}
							label={
								<FormattedMessage
									id="SheetSettings.LimitTextBox"
									defaultMessage="Limit text in cells"
								/>
							}
						/>
					</div>
					<TextField
						label={
							<FormattedMessage id="SheetSettings.LimitTextField" defaultMessage="Max. Characters" />
						}
						type="number"
						variant="outlined"
						value={maxChars}
						margin="normal"
						size="small"
						style={{
							marginTop: '5px',
							marginLeft: '25px',
							width: '120px'
						}}
						inputProps={{
							min: 1,
							max: Number.MAX_SAFE_INTEGER,
							step: 1
						}}
						onChange={(event) => setMaxChars(Number(event.target.value))}
						onBlur={(event) => {
							const maxchars = Number(event.target.value);
							if (maxchars < Number.MAX_SAFE_INTEGER) {
								handleSetting({ sheet: { maxchars } });
							}
						}}
						error={maxChars > Number.MAX_SAFE_INTEGER}
						helperText={
							maxChars > Number.MAX_SAFE_INTEGER ? (
								<FormattedMessage
									id="SheetSettings.LimitTextError"
									defaultMessage="At most {maxchars} characters allowed!"
									values={{
										maxchars: Number.MAX_SAFE_INTEGER
									}}
								/>
							) : (
								''
							)
						}
					/>
				</FormGroup>]}
			</FormGroup>
		</SettingsPaper>
	);
}

function mapDispatchToProps(dispatch) {
	return bindActionCreators({ ...Actions }, dispatch);
}

// export const ShareSettings = withAppData(({ machine, dispatchAppData }) => ({
// 	machineId: machine.id,
// 	settings: machine.settings,
// 	viewSettings: machine.settings.view,
// 	updateShareSettings: (settings) => dispatchAppData({ type: 'update-machine-settings', data: settings })
// }))(connect(null, mapDispatchToProps)(ShareSettingsComponent));
export const SheetSettings = withAppData(({ connections }) => ({ connections }))(
	connect(null, mapDispatchToProps)(SheetSettingsComponent));
